import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '@paypalcorp/pp-react-theme';
import clsx from 'clsx';
import { CheckCircleAltIcon } from '@paypalcorp/pp-react-icons';
import '../__generated__/styles/toast.css';

var styles = {"ppvx_toast":"ppvx_toast___1-8-6","ppvx_toast-container":"ppvx_toast-container___1-8-6","ppvx_toast-container--show":"ppvx_toast-container--show___1-8-6","fadein":"fadein___1-8-6","ppvx_toast-container--hide":"ppvx_toast-container--hide___1-8-6","fadeout":"fadeout___1-8-6","ppvx_toast--type_success":"ppvx_toast--type_success___1-8-6","ppvx_toast__success-icon":"ppvx_toast__success-icon___1-8-6","ppvx_toast--type_success-with-svg-icon":"ppvx_toast--type_success-with-svg-icon___1-8-6","ppvx--v2":"ppvx--v2___1-8-6","ppvx--v1":"ppvx--v1___1-8-6"};

var _excluded = ["id", "message", "isSuccess", "duration", "onClose", "direction", "className", "containerClassName", "containerRef", "theme"];
var defaultDuration = 7000;

var Toast = function (_ref) {
  var _clsx, _clsx2;

  var _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'Toast' : _ref$id,
      message = _ref.message,
      _ref$isSuccess = _ref.isSuccess,
      isSuccess = _ref$isSuccess === void 0 ? false : _ref$isSuccess,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? defaultDuration : _ref$duration,
      onClose = _ref.onClose,
      direction = _ref.direction,
      className = _ref.className,
      containerClassName = _ref.containerClassName,
      containerRef = _ref.containerRef,
      theme = _ref.theme,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _theme = useTheme('Toast', theme);

  if (_theme !== 'v2') {
    console.warn('Toasts 1.0 styles are deprecated. Use Toasts 2.0 styles and refer to our Migration Guide on how to use 2.0 styles/components.');
  }

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      showState = _useState2[0],
      setShowState = _useState2[1];

  var startY = null;
  var startX = null;
  var toastRef = useRef();

  var getWrapperCurrent = function (w) {
    return (
      /* istanbul ignore next */
      w && w.current ? w.current : null
    );
  };

  var autoHideTimer = getWrapperCurrent(useRef());

  var handleClose = function () {
    setShowState(0);
    clearTimeout(autoHideTimer);
    startX = null;
    startY = null;
  };

  var handleKey = function (evt) {
    var keyCode = evt.key || evt.keyCode;

    switch (keyCode) {
      case 'Escape':
      case 'Esc':
        handleClose();
        break;
    }
  };
  /* istanbul ignore next */


  var handleTouchStart = function (evt) {
    //prevent from scrolling
    evt.preventDefault();
    /* istanbul ignore next */

    if (!startY && !startX) {
      var firstTouch = evt.changedTouches[0];
      startY = firstTouch.pageY;
      startX = firstTouch.pageX;
    }
  };
  /* istanbul ignore next */


  var handleTouchEnd = function (evt) {
    var endTouch = evt.changedTouches[0];
    var endX = endTouch.pageX;
    var endY = endTouch.pageY;
    var xDiff = startX - endX;
    var yDiff = startY - endY;

    if (Math.abs(yDiff) > Math.abs(xDiff)) {
      // means up/down is most significant
      if (yDiff > 0) {
        // swipe up
        handleClose();
      }
    }

    startX = null;
    startY = null;
  };

  useEffect(function () {
    if (showState < 0) {
      setShowState(1);
    }

    document.addEventListener('keydown', handleKey);
    var toast = getWrapperCurrent(toastRef); // Adding native event since evt.preventDefault (for preventing from scrolling
    // while swiping on toast up) is not possible using synthetic event

    toast.addEventListener('touchstart', handleTouchStart);
    return function () {
      document.removeEventListener('keydown', handleKey);
      toast.removeEventListener('touchstart', handleTouchStart);
      clearTimeout(autoHideTimer);
    };
  }, [showState]);
  /* istanbul ignore next */

  var toastContainerClasses = clsx(styles['ppvx_toast-container'], (_clsx = {}, _defineProperty(_clsx, styles['ppvx_toast-container--show'], showState), _defineProperty(_clsx, styles['ppvx_toast-container--hide'], !showState), _defineProperty(_clsx, styles['ppvx--v2'], _theme && _theme === 'v2'), _clsx), containerClassName);
  var toastClasses = clsx(styles.ppvx_toast, className, (_clsx2 = {}, _defineProperty(_clsx2, styles['ppvx_toast--type_success'], isSuccess), _defineProperty(_clsx2, styles['ppvx_toast--type_success-with-svg-icon'], isSuccess), _clsx2));
  var successIcon = isSuccess && /*#__PURE__*/React.createElement(CheckCircleAltIcon, {
    className: styles['ppvx_toast__success-icon']
  });
  return ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    dir: direction,
    className: toastContainerClasses,
    "aria-hidden": showState <= 0,
    role: "alert",
    onAnimationEnd: function onAnimatonEnd() {
      if (showState === 1) {
        var timeOut = duration > defaultDuration ? duration : defaultDuration;
        autoHideTimer = setTimeout(handleClose, timeOut);
      } else if (typeof onClose === 'function') {
        onClose();
      }
    }
  }, rest), /*#__PURE__*/React.createElement("div", {
    ref: toastRef,
    className: toastClasses,
    onTouchEnd: handleTouchEnd
  }, successIcon, message)), getWrapperCurrent(containerRef) || document.body);
};

export { Toast };
